"use client";

import { SiteLink } from "@i18n/SiteLink";
import {
	type BillingInterval,
	type PerplexityTier,
	formatUsd,
	getPerplexityTiers,
	perplexityFromMonthly,
} from "@saas/payments/lib/pricing-v2";
import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import { ArrowRightIcon, CheckIcon, MinusIcon, PlusIcon } from "lucide-react";
import { useTranslations } from "next-intl";

type CoverageCell = "included" | "addOn" | "enterprise" | "none";

interface PlatformCoverageRow {
	name: string;
	logo?: string;
	daily: CoverageCell;
	dailyNote?: string;
	report: CoverageCell;
}

const CELL_LABEL: Record<CoverageCell, string> = {
	included: "Included",
	addOn: "Add-on",
	enterprise: "Enterprise",
	none: "Not included",
};

/**
 * Perplexity and Claude add-on cards plus the platform coverage table, for
 * /pricing/add-ons. The table makes one thing unambiguous: Daily Prompt
 * Tracking runs on ChatGPT, Gemini and Google AI Overviews; Perplexity and
 * Claude are add-ons; Copilot and DeepSeek are Enterprise only. Perplexity
 * prices follow the page's billing toggle and come from the add-on registry.
 */
export function AddOnsSection({
	interval,
	className,
}: {
	interval: BillingInterval;
	className?: string;
}) {
	return (
		<div className={cn("grid gap-6 lg:grid-cols-5", className)}>
			<div className="min-w-0 space-y-4 lg:col-span-3">
				<PerplexityCard interval={interval} />
				<ClaudeCard />
			</div>
			<PlatformCoverageTable className="lg:col-span-2" />
		</div>
	);
}

function PerplexityCard({ interval }: { interval: BillingInterval }) {
	const tiers = getPerplexityTiers();
	return (
		<div className="rounded-2xl border bg-card p-5">
			<AddOnHeading
				logo="/images/perplexity-logo.svg"
				name="Perplexity daily tracking"
				body="Your tracked prompts run on Perplexity every day, alongside the platforms your plan covers. Priced per property and sized to your plan."
			/>
			<div className="mt-4 grid gap-2 sm:grid-cols-3">
				{tiers.map((tier) => (
					<TierPrice
						key={tier.addOnKey}
						tier={tier}
						interval={interval}
					/>
				))}
			</div>
			<Button variant="light" size="sm" className="mt-4" asChild>
				<SiteLink href="/contact">
					<PlusIcon className="mr-1 size-3.5" aria-hidden />
					Add to your plan
				</SiteLink>
			</Button>
		</div>
	);
}

function TierPrice({
	tier,
	interval,
}: {
	tier: PerplexityTier;
	interval: BillingInterval;
}) {
	const t = useTranslations();
	const price = tier.prices[interval] ?? tier.prices.month;
	if (!price) return null;
	return (
		<div className="rounded-xl border bg-muted/40 px-2 py-2.5 sm:px-3">
			{tier.planKey ? (
				<p className="text-xs font-semibold text-muted-foreground">
					With {t(`pricing.products.${tier.planKey}.title`)}
				</p>
			) : null}
			<p className="mt-0.5">
				<span className="text-lg font-extrabold tracking-tight">
					{formatUsd(price.perMonth)}
				</span>
				<span className="text-xs text-muted-foreground">/mo</span>
			</p>
			<p className="text-xs text-muted-foreground">
				{tier.prompts.toLocaleString("en-US")} prompts daily
			</p>
			{interval === "month" ? null : (
				<p className="text-xs text-muted-foreground">
					Billed {formatUsd(price.billed)}{" "}
					{interval === "quarter" ? "quarterly" : "yearly"}
				</p>
			)}
		</div>
	);
}

function ClaudeCard() {
	return (
		<div className="rounded-2xl border bg-card p-5">
			<AddOnHeading
				logo="/images/claude-logo.svg"
				name="Claude tracking"
				body="Your tracked prompts run on Claude every day. We set it up for each property, so talk to us and we'll get it running."
			/>
			<Button variant="light" size="sm" className="mt-4" asChild>
				<SiteLink href="/contact">
					Talk to us
					<ArrowRightIcon className="ml-1 size-3.5" aria-hidden />
				</SiteLink>
			</Button>
		</div>
	);
}

function AddOnHeading({
	logo,
	name,
	body,
}: {
	logo: string;
	name: string;
	body: string;
}) {
	return (
		<div className="flex min-w-0 items-start gap-3">
			<span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-white">
				{/* eslint-disable-next-line @next/next/no-img-element */}
				<img src={logo} alt="" className="size-5 object-contain" />
			</span>
			<div className="min-w-0">
				<h3 className="font-bold tracking-tight">{name}</h3>
				<p className="mt-1 text-sm text-muted-foreground">{body}</p>
			</div>
		</div>
	);
}

function coverageRows(): PlatformCoverageRow[] {
	const from = perplexityFromMonthly();
	return [
		{
			name: "ChatGPT",
			logo: "/images/chatgpt-logo.svg",
			daily: "included",
			report: "included",
		},
		{
			name: "Gemini",
			logo: "/images/google-gemini-logo.svg",
			daily: "included",
			report: "included",
		},
		{
			name: "Google AI Overviews",
			logo: "/images/google-logo.svg",
			daily: "included",
			report: "included",
		},
		{
			name: "Perplexity",
			logo: "/images/perplexity-logo.svg",
			daily: "addOn",
			dailyNote: from === null ? undefined : `From ${formatUsd(from)}/mo`,
			report: "included",
		},
		{
			name: "Claude",
			logo: "/images/claude-logo.svg",
			daily: "addOn",
			dailyNote: "Talk to us",
			report: "included",
		},
		{ name: "Microsoft Copilot", daily: "enterprise", report: "none" },
		{ name: "DeepSeek", daily: "enterprise", report: "none" },
	];
}

function CoverageValue({ cell, note }: { cell: CoverageCell; note?: string }) {
	if (cell === "included") {
		return (
			<span className="inline-flex items-center gap-1 font-semibold">
				<CheckIcon className="size-4 text-primary" aria-hidden />
				{CELL_LABEL[cell]}
			</span>
		);
	}
	if (cell === "none") {
		return (
			<span className="inline-flex items-center gap-1 text-muted-foreground">
				<MinusIcon className="size-4" aria-hidden />
				<span className="sr-only">{CELL_LABEL[cell]}</span>
			</span>
		);
	}
	return (
		<span className="flex flex-col">
			<span className="inline-flex items-center gap-1 text-muted-foreground">
				<PlusIcon className="size-3.5" aria-hidden />
				{CELL_LABEL[cell]}
			</span>
			{note ? (
				<span className="text-xs text-muted-foreground">{note}</span>
			) : null}
		</span>
	);
}

export function PlatformCoverageTable({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				"min-w-0 overflow-hidden rounded-2xl border bg-card",
				className,
			)}
		>
			<div className="border-b px-5 py-4">
				<h3 className="font-bold tracking-tight">
					Which AI platforms we track
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					Daily tracking follows your prompts every day. The AI
					Visibility Report is a broad audit you run when you need it.
				</p>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full text-xs sm:text-sm">
					<thead>
						<tr className="border-b text-left text-xs text-muted-foreground">
							<th
								scope="col"
								className="py-2.5 pr-2 pl-4 font-semibold sm:px-5"
							>
								Platform
							</th>
							<th
								scope="col"
								className="px-2 py-2.5 font-semibold sm:px-3"
							>
								Daily Prompt Tracking
							</th>
							<th
								scope="col"
								className="px-2 py-2.5 font-semibold sm:px-3"
							>
								AI Visibility Report
							</th>
						</tr>
					</thead>
					<tbody>
						{coverageRows().map((row) => (
							<tr
								key={row.name}
								className="border-b last:border-0"
							>
								<th
									scope="row"
									className="py-2.5 pr-2 pl-4 text-left font-medium sm:px-5"
								>
									<span className="flex items-center gap-2">
										{row.logo ? (
											// eslint-disable-next-line @next/next/no-img-element
											<img
												src={row.logo}
												alt=""
												className="size-4 shrink-0 rounded-[3px] bg-white object-contain p-px"
											/>
										) : (
											<span
												className="size-4 shrink-0"
												aria-hidden
											/>
										)}
										{row.name}
									</span>
								</th>
								<td className="px-2 py-2.5 sm:px-3">
									<CoverageValue
										cell={row.daily}
										note={row.dailyNote}
									/>
								</td>
								<td className="px-2 py-2.5 sm:px-3">
									<CoverageValue cell={row.report} />
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
